import { GEAR_STAT_IDS, GearStat, StatUse } from "@highmarch/domain";
import { icon } from "./iconsKit";
import { fmtCompact } from "./kit";
import { resourceIconUrl } from "./portraits";
export const RARITY_LABEL = ["Common", "Uncommon", "Rare", "Epic", "Legendary", "Mythic"];
const STAT_LABEL: Record<string, string> = { damage: "damage", range: "range", fireRate: "fire rate", hp: "fortress HP", points: "points" };

export const pct = (f: { toNumber(): number }): string => `+${(f.toNumber() * 100).toFixed(f.toNumber() * 100 >= 10 ? 0 : 1)}%`;
/** Decimals a before → after pair may grow to before it gives up (gear steps are never under 0.05 of a point). */
const STEP_MAX_DECIMALS = 2;
const pctAt = (f: { toNumber(): number }, d: number): string => (d === 0 ? pct(f) : `+${(f.toNumber() * 100).toFixed(d)}%`);
/**
 * A before → after pair in percent with just enough decimals that a real change reads as one: whole numbers where
 * they differ ("+15% → +17%"), else tenths ("+15.0% → +15.2%"), else hundredths. Equal values print equal.
 */
export function pctStep(before: { toNumber(): number }, after: { toNumber(): number }): [string, string] {
  for (let d = 0; d < STEP_MAX_DECIMALS; d++) {
    const a = pctAt(before, d), b = pctAt(after, d);
    if (a !== b) return [a, b];
  }
  return [pctAt(before, STEP_MAX_DECIMALS), pctAt(after, STEP_MAX_DECIMALS)];
}
/** What a gear stat does on an owner that uses it only at tier 4 or never (domain gearStatUse); "" where it counts. */
export function useNote(use: StatUse, owner: string): string {
  return use === StatUse.Full ? "" : use === StatUse.Ascended ? ` · tier 4 only on ${owner}` : ` · no use on ${owner}`;
}
export const fmtStat = (s: GearStat): string => STAT_LABEL[GEAR_STAT_IDS[s]] ?? GEAR_STAT_IDS[s];

/** Kit glyph per gear slot, drawn in place of the painted icon when that image fails to load. */
export const SLOT_GLYPH: Record<string, string> = { mount: "tower", sight: "target", ammo: "bolt", charm: "star", wall: "shield", banner: "flag", forge: "flame", relic: "crystal" };

/**
 * Swaps a gear icon `<img>` for the slot's kit glyph if the image fails — a missing or un-inlined PNG then reads as
 * an icon, not as the browser's broken-image box. The listener is attached in code, not as an inline `onerror`
 * attribute, so it survives a strict CSP.
 */
export function gearIconFallback(img: HTMLImageElement | null, slotId: string, size: number): void {
  img?.addEventListener("error", () => {
    const span = document.createElement("span");
    span.className = "gear-fallback";
    span.innerHTML = icon(SLOT_GLYPH[slotId] ?? "bag", size);
    img.replaceWith(span);
  }, { once: true });
}

/** An upgrade's gold cost with its coins, for a line of small text. */
export function describeCost(gold: number): string {
  return `<span class="res au"><img class="rimg" decoding="sync" src="${resourceIconUrl("gold")}" alt="gold"> ${fmtCompact(gold)}</span>`;
}
